import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FieldArray } from 'formik';
import cs from 'classnames';
import { I18N } from 'commons';
import styled from 'styled-components';
import HelpAndAnswerTip from 'containers/app/HelpAndAnswerTip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import v from 'scss/v';

const LocationMatchersContainer = styled.div`
  overflow-y: auto;
`;

const Sidebar = ({
  geocodingAddresses,
  setGeocodingAddresses,
  geocodingType,
  setPosition,
  values,
  setFieldValue,
  saveGeocodingAddressResult,
  phoneModelBtnShow,
  setPhoneModelBtnShow,
  setIsShowFreeDragPoint,
  setAddress,
  formAddress
}) => {
  const { street, suite, city, state, postalCode, countryName, locationName, country } = formAddress;
  const [containerHeight, setContainerHeight] = useState('54vh');
  const verifyLocationHeaderRef = useRef();
  const verifyLocationInfoRef = useRef();

  const updateAddressData = (newAddress) => {
    const { streetAddress, suite: unit, city: newCity, postalCode: selectedPostalCode, adminDistrict } = newAddress;
    setAddress({
      locationName,
      street: streetAddress || street,
      suite: unit || suite,
      city: newCity || city,
      postalCode: selectedPostalCode || postalCode,
      state: adminDistrict || state,
      country,
      countryName,
    });
  };

  useEffect(() => {
    if (geocodingAddresses.length) {
      updateAddressData(geocodingAddresses[0]);
    }
    const dynamicHeight = document.getElementsByClassName('modal-body')[0].offsetHeight - verifyLocationHeaderRef.current.offsetHeight - (verifyLocationInfoRef.current.offsetHeight * 2) - 80;
    setContainerHeight(`${dynamicHeight}px`);
  }, []);

  return (
    <div className="col-sm-3 pr-0 pl-0">
      <HelpAndAnswerTip type="verifyLocations" content="ssp.help.default.verifyLocations" />
      <h3 className="text-word-break clearfix verify-location-header" ref={verifyLocationHeaderRef}>
        {!phoneModelBtnShow && (
          <a
            href="javascript:void(0)"
            className="verify-location-arrow pull-left"
            onClick={() => setPhoneModelBtnShow(true)}
          >
            {' '}
            <I18N>button.back</I18N>
            <div className="leftarrow" />
          </a>
        )}

        <span className="pull-left verify-location-title">{locationName}</span>
        {phoneModelBtnShow && (
          <a
            href="javascript:void(0)"
            className="verify-location-arrow pull-right"
            onClick={() => setPhoneModelBtnShow(false)}
          >
            {' '}
            <I18N>ssp.location.map</I18N>
            <div className="rightarrow" />
          </a>
        )}
      </h3>
      <div id="addressRegion" style={{ display: phoneModelBtnShow ? 'block' : 'none' }} className="side-bar-container">
        <p className="verify-location-info" ref={verifyLocationInfoRef}>
          <I18N>ssp.location.enter</I18N>:
          {Object.values({
            street,
            suite,
            city,
            state,
            postalCode,
            countryName,
          })
            .filter(str => !!str)
            .join(', ')}
        </p>
        <FieldArray
          name="geocodingAddress"
          render={() => (
            <LocationMatchersContainer className="verify-location-matches" style={{ height: containerHeight }}>
              {
                !saveGeocodingAddressResult && (
                  <div className="check-geo-p alert alert-danger text-left" role="alert">
                    <FontAwesomeIcon icon={faExclamationCircle} color={v('$red-7')} width="1em" height="1em" />
                    <div className="ml-2">
                      <b><I18N>home.alert.box.title.error</I18N></b>
                      <p className="mb-0"><I18N>ssp.map.out.of.area.warning</I18N></p>
                    </div>
                  </div>
                )}
              {geocodingType !== 'Custom' && !!geocodingAddresses.length && (
                <p>
                  <I18N>ssp.location.possible.alert</I18N>
                </p>
              )}
              <fieldset>
                <legend><I18N>location.modal.verify.select.location</I18N></legend>
                {geocodingAddresses.map((marker, index) => (
                  <div className="radio" key={`varify-location-${String(index)}`}>
                    <label className={cs({ 'font-normal': values.selectedIndex !== index })} htmlFor={`addressRadio${index}`}>
                      <input
                        id={`addressRadio${index}`}
                        className="js_raido"
                        type="radio"
                        name="selectedIndex"
                        value={index}
                        checked={values.selectedIndex === index}
                        onChange={() => {
                          setGeocodingAddresses(geocodingAddresses.map((marker, secIndex) => {
                            marker.active = false;
                            if (secIndex === index) marker.active = true;
                            return marker;
                          }));
                          updateAddressData(marker);
                          setPosition(marker.position);
                          setFieldValue('selectedIndex', index);
                          setFieldValue('selectedIndexSecond', 1);
                          setIsShowFreeDragPoint(false);
                        }}
                      />
                      {marker.addressName}
                    </label>
                  </div>
                ))}
                <div>
                  <div className="radio">
                    <label className={values.selectedIndexSecond === 0 ? '' : 'font-normal'} htmlFor="showUsRadio">
                      <input
                        id="showUsRadio"
                        className="js_raido"
                        type="radio"
                        name="selectedIndexShowUs"
                        value={0}
                        checked={values.selectedIndexSecond === 0}
                        onChange={() => {
                          setGeocodingAddresses(geocodingAddresses.map(marker => {
                            marker.active = false;
                            return marker;
                          }));
                          updateAddressData({});
                          if (geocodingAddresses.length) {
                            setPosition(geocodingAddresses[0].position);
                          }

                          setFieldValue('selectedIndex', -1);
                          setFieldValue('selectedIndexSecond', 0);
                          setIsShowFreeDragPoint(true);
                        }}
                      />
                      <I18N>ssp.location.show.us.alert</I18N>
                    </label>
                  </div>
                </div>
              </fieldset>
            </LocationMatchersContainer>
          )}
        />
      </div>
    </div>
  );
};

Sidebar.propTypes = {
  geocodingAddresses: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  setGeocodingAddresses: PropTypes.func.isRequired,
  geocodingType: PropTypes.string.isRequired,
  setPosition: PropTypes.func.isRequired,
  values: PropTypes.shape().isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setAddress: PropTypes.func.isRequired,
  saveGeocodingAddressResult: PropTypes.bool.isRequired,
  phoneModelBtnShow: PropTypes.bool.isRequired,
  setPhoneModelBtnShow: PropTypes.func.isRequired,
  setIsShowFreeDragPoint: PropTypes.func.isRequired,
  formAddress: PropTypes.shape().isRequired,
};


export default Sidebar;
